import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MODULE_TYPES } from '@ohif/core';
import filesToStudies from '../routes/Local/filesToStudies';
import { extensionManager } from '../App';

const STUDY_LIST = [
  // {
  //   study_instance_uid: '1.3.76.8.100.270659.1118650',
  //   instances: [
  //     '/Dicom-images/IM-0019-56837920-0001.dcm',
  //     '/Dicom-images/IM-0019-6527200-0001.dcm',
  //     '/Dicom-images/IM-0019-77762176-0001.dcm',
  //   ],
  // },
];

const StudyViewer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const studyInstanceUid = searchParams.get('study-instance-uid');

  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.style.backgroundColor = 'hsl(var(--background))';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    if (!studyInstanceUid) {
      setError('Missing study-instance-uid query parameter.');
      setLoading(false);
      return;
    }

    const matchingStudy = STUDY_LIST.find(
      s => s.study_instance_uid === studyInstanceUid
    );

    if (!matchingStudy) {
      setError(`No study found for UID: ${studyInstanceUid}`);
      setLoading(false);
      return;
    }

    getSeriesInstances(matchingStudy.instances);
  }, [studyInstanceUid]);

  const getSeriesInstances = async (instanceUrls: string[]) => {
    debugger;
    try {
      setLoading(true);
      setProgress(0);

      if (instanceUrls.length === 0) {
        throw new Error('No instance URLs found for the selected study');
      }

      const totalFiles = instanceUrls.length;
      let completedFiles = 0;

      // Fetch DICOM files in parallel with retry logic
      const fetchedFiles = await Promise.all(
        instanceUrls.map(async (url, idx) => {
          let retries = 0;
          while (retries < 3) {
            try {
              const res = await fetch(url);
              if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
              const blob = await res.blob();
              const fileName = url.split('/').pop();
              const file = new File([blob], fileName || `dicom-${idx}.dcm`, {
                type: blob.type || 'application/dicom',
              });
              completedFiles++;
              setProgress(Math.round((completedFiles / totalFiles) * 100));
              return file;
            } catch (fetchError) {
              retries++;
              if (retries === 3) {
                console.error(`Failed to fetch ${url} after ${retries} attempts`);
                completedFiles++;
                setProgress(Math.round((completedFiles / totalFiles) * 100));
                return null;
              }
              await new Promise(resolve => setTimeout(resolve, 1000 * retries));
            }
          }
          return null;
        })
      );

      // Filter out failed fetches
      const validFiles = fetchedFiles.filter((file): file is File => file !== null);

      if (validFiles.length === 0) {
        throw new Error('No valid DICOM files were fetched');
      }

      // Initialize the dicom local dataSource
      const dataSourceModules = extensionManager.modules[MODULE_TYPES.DATA_SOURCE];
      const localDataSources = dataSourceModules.reduce((acc: any[], curr: any) => {
        const mods: any[] = [];
        curr.module.forEach((mod: any) => {
          if (mod.type === 'localApi') {
            mods.push(mod);
          }
        });
        return acc.concat(mods);
      }, []);

      const firstLocalDataSource = localDataSources[0];
      const dataSource = firstLocalDataSource.createDataSource({});

      // Clear previous and process new files
      await filesToStudies([], dataSource);
      const studies = await filesToStudies(validFiles, dataSource);

      const query = new URLSearchParams();
      studies.forEach((id: string) => query.append('StudyInstanceUIDs', id));
      query.append('datasources', 'dicomlocal');
      query.append('hangingprotocolid', 'fourUpStack');
      query.append('t', Date.now().toString());

      // Navigate to segmentation viewer with single stack layout
      navigate(`/segmentation/dicomlocal?${decodeURIComponent(query.toString())}`);
    } catch (err: any) {
      console.error('Failed to process series:', err);
      setError(err.message || 'Failed to load study.');
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: 'hsl(var(--background))',
        minHeight: '100vh',
        color: 'hsl(var(--foreground))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {error ? (
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              color: '#ef4444',
              padding: '1rem 1.5rem',
              borderRadius: '8px',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              fontSize: '0.95rem',
              maxWidth: '400px',
            }}
          >
            {error}
          </div>
          <button
            onClick={() => navigate('/')}
            style={{
              marginTop: '1.5rem',
              padding: '0.6rem 1.5rem',
              backgroundColor: 'hsl(var(--primary))',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Back to Upload
          </button>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              border: '3px solid hsl(var(--primary)/0.2)',
              borderTopColor: 'hsl(var(--primary))',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1.5rem',
            }}
          />
          <p style={{ color: 'hsl(var(--foreground))', fontWeight: '500', fontSize: '1.1rem', margin: '0 0 0.5rem' }}>
            Loading DICOM Study...
          </p>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem', margin: 0 }}>
            {progress}% complete
          </p>
          {/* Progress bar */}
          <div
            style={{
              width: '240px',
              height: '4px',
              backgroundColor: 'rgba(148, 163, 184, 0.2)',
              borderRadius: '2px',
              marginTop: '1rem',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor: 'hsl(var(--primary))',
                borderRadius: '2px',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>
      ) : null}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default StudyViewer;
